import { useState, useEffect, useRef } from 'react';

// Custom hook for Speech Recognition with 3-second pause detection
export const useVoiceRecognition = ({ onResult, onError, onStart, onEnd, isListening, isSpeaking, isUploading, isTyping }) => {
  const [recognition, setRecognition] = useState(null);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const currentTranscriptRef = useRef('');
  const isProcessingRef = useRef(false);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recog = new SpeechRec();
      
      // Enable continuous listening with interim results
      recog.continuous = true;
      recog.interimResults = true;
      recog.lang = 'en-US';
      recog.maxAlternatives = 1;

      recog.onstart = () => {
        console.log('Recognition started');
        currentTranscriptRef.current = '';
        isProcessingRef.current = false;
        onStart();
      };

      recog.onend = () => {
        console.log('Recognition ended');
        // Clear any pending silence timer
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        onEnd();
      };

      recog.onerror = (e) => {
        console.error('Speech recognition error:', e.error);
        // Clear silence timer on error
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        currentTranscriptRef.current = '';
        isProcessingRef.current = false;
        onError(e.error, isListening);
      };

      recog.onresult = (e) => {
        let interimTranscript = '';
        let finalTranscript = '';

        // Process all results
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const transcript = e.results[i][0].transcript;
          if (e.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update current transcript with final + interim
        const currentTranscript = (finalTranscript + interimTranscript).trim();
        currentTranscriptRef.current = currentTranscript;

        console.log('Current transcript:', currentTranscript);
        console.log('Final part:', finalTranscript);
        console.log('Interim part:', interimTranscript);

        // Clear existing silence timer
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }

        // If we have content and it's not already being processed, start the 3-second timer
        if (currentTranscript.length > 0 && !isProcessingRef.current) {
          silenceTimerRef.current = setTimeout(() => {
            if (currentTranscriptRef.current.trim() && !isProcessingRef.current) {
              console.log('3 seconds of silence detected, processing:', currentTranscriptRef.current);
              isProcessingRef.current = true;
              
              // Stop recognition before processing
              if (recognitionRef.current) {
                recognitionRef.current.stop();
              }
              
              // Send the result for processing
              onResult(currentTranscriptRef.current.trim());
              
              // Clear the transcript
              currentTranscriptRef.current = '';
            }
          }, 3000); // 3 seconds
        }
      };

      // Handle silence detection (no speech detected)
      recog.onspeechstart = () => {
        console.log('Speech detected');
        // Clear silence timer when speech starts
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
      };

      recog.onspeechend = () => {
        console.log('Speech ended, waiting for 3 seconds...');
        // Speech ended, but we'll wait for the silence timer to trigger
        // Don't immediately process - let the timer handle it
      };

      recog.onnomatch = () => {
        console.log('No speech recognized');
        // If no match and we have previous transcript, still honor the silence timer
      };

      setRecognition(recog);
      recognitionRef.current = recog;
    } else {
      console.warn('Speech recognition not supported in this browser');
      onError('not-supported', false);
    }

    return () => {
      // Cleanup
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    // Only start listening if recognition is ready, not already listening,
    // AI is not speaking, not uploading, and user is not typing.
    if (recognitionRef.current && !isListening && !isSpeaking && !isUploading && !isTyping) {
      try {
        // Reset state
        currentTranscriptRef.current = '';
        isProcessingRef.current = false;
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        
        recognitionRef.current.start();
      } catch (error) {
        console.error('Error starting recognition:', error);
        if (error.name !== 'InvalidStateError') {
          onError(error.name, isListening);
        }
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      // Clear any pending timers
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      
      // If we have accumulated transcript and haven't processed it yet, process it now
      if (currentTranscriptRef.current.trim() && !isProcessingRef.current) {
        console.log('Stopping recognition, processing current transcript:', currentTranscriptRef.current);
        isProcessingRef.current = true;
        onResult(currentTranscriptRef.current.trim());
        currentTranscriptRef.current = '';
      }
      
      recognitionRef.current.stop();
    }
  };

  const forceProcess = () => {
    // Manual trigger to process current transcript (useful for debugging)
    if (currentTranscriptRef.current.trim() && !isProcessingRef.current) {
      console.log('Force processing:', currentTranscriptRef.current);
      isProcessingRef.current = true;
      
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
      
      onResult(currentTranscriptRef.current.trim());
      currentTranscriptRef.current = '';
    }
  };

  return { recognition, startListening, stopListening, forceProcess };
};

// Function for Speech Synthesis (TTS) - unchanged
export const playWithSpeechSynthesis = (text, setIsSpeakingCallback) => {
  return new Promise((resolve) => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel(); // Cancel any existing speech

      setIsSpeakingCallback(true);

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      utterance.onend = () => {
        console.log('Speech ended');
        setIsSpeakingCallback(false);
        resolve();
      };

      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error);
        setIsSpeakingCallback(false);
        resolve();
      };

      speechSynthesis.speak(utterance);
    } else {
      console.warn('Speech synthesis not supported.');
      setIsSpeakingCallback(false);
      resolve();
    }
  });
};